import {
  assign,
  ConditionPredicate,
  Interpreter,
  createMachine as createXstateMachine,
  MachineConfig,
  send,
  Sender,
  ServiceConfig,
} from 'xstate';
import * as R from 'ramda';
import {InvariantError} from 'ts-invariant';
import {ApiErrorKind} from '@practicegenius/http-clients-js/src/types';
import {NavigationEvent} from '@practicegenius/domain/src/machines/events';
import {objectKeysToCamelCase} from '@practicegenius/pg-util/src/ObjectUtils';
import {IMT, snakeCase} from '@practicegenius/pg-util/src/StringUtils';
import {Require} from '@practicegenius/pg-util/src/TypescriptUtils';
import {parseRegistrationRoute, routeCanBeSkippedTo, routes} from '../routes';
import {
  nextRequirementScreen,
  remainingPreRegistrationMethodRequirements,
} from '../screens/utils';
import {machine as nameVerificationMachine} from './nameVerification';
import {
  Context,
  defaultContext,
  Event,
  EventSchema,
  State,
  StateSchema,
} from './machineTypes';
import * as sharedMachineOptions from './sharedMachineOptions';
import lang from '../lang';
import {
  AccountRelationship,
  AlreadyRegisteredHint,
  RegistrationData,
  RegistrationReason,
} from '../data/types';
import {cleanCardNumber} from '../data/utils';
import {handleRegistrationValidationError} from './sharedMachineOptions';
import {transformRegistration, transformPractice} from '../data/transformers';

const nextRequirementIs = (route: string) => (context: Context) => {
  return (
    nextRequirementScreen({
      location: context.navigator.location,
      registrationCompleted: context.registrationCompleted ?? false,
      remainingRequirements: context.remainingRequirements ?? [],
      draftAccount: context.account ?? {},
    }) === route
  );
};

const navigatorParamsHas: ConditionPredicate<Context, EventSchema> = (
  context,
  event,
  meta
) => {
  const queryString = context.navigator.location.params;
  const parameters = R.propOr([], 'parameters', meta.cond) as string[];

  if (parameters.length === 0) {
    throw new InvariantError(
      'Tried to use guard "queryStringHas" with no provided parameters to search for. Check the "queryStringHas" guard and conditions that it.'
    );
  }

  const transformers = R.propOr({}, 'transform', meta.cond) as {
    [param: string]: (value: string) => unknown;
  };

  return parameters.every(parameter => {
    const transformer = transformers[parameter] ?? R.identity;

    return (
      parameter in queryString &&
      Boolean(transformer(queryString[parameter] as string))
    );
  });
};

const currentPathMatches: ConditionPredicate<Context, EventSchema> = (
  context,
  event,
  {cond}
) => {
  if (!('route' in cond)) {
    throw new InvariantError(
      'Cannot match current path without "route" meta. Check "currentPathMatches" guard.'
    );
  }

  // Match array route
  if (Array.isArray(cond.route)) {
    return cond.route.some(route => context.navigator.isCurrentRoute(route));
  }

  return context.navigator.isCurrentRoute(cond.route);
};

const everyGuardPasses =
  (
    ...guards: (Record<string, unknown> & {
      type: ConditionPredicate<Context, EventSchema>;
    })[]
  ): ConditionPredicate<Context, EventSchema> =>
  (context, event) =>
    guards.every(({type: predicate, ...meta}) =>
      predicate(context, event, {
        // @ts-ignore - This doesn't match the shape of a cond, since we're sorta hacking this together
        cond: meta,
      })
    );

const errorAndRedirect = (
  showAlert: Context['showAlert'],
  redirectToLogin: Context['redirect']['toLogin']
) => {
  showAlert(lang.general.redirectErrorMessage);
  redirectToLogin();
};

/**
 * An unsuccessful attempt at booting registration from card number,
 * business code, or invite requires us to go back to the link
 * practice page to try again.
 */
const bootUserConnectionOnError = {
  target: '#link-practice',
  actions: [
    'reportError',
    {type: 'replaceRouteWith', route: routes.LinkPractice},
  ],
};
const bootUserConnectionOnUpdateData = {
  target: State.idle,
  actions: 'updateData',
};
const bootUserConnectionOnHasValidationErrors = {
  target: '#link-practice',
  actions: [
    'updateValidationErrors',
    {type: 'replaceRouteWith', route: routes.LinkPractice},
  ],
};

const makeConnectUserIdentifierService =
  (
    context: Context,
    endpoint: string,
    payload: Record<string, any>,
    genericErrorMessage: string
  ) =>
  async (send: Sender<EventSchema>) => {
    (await context.apiClient.post(`${endpoint}?include=practice`, payload))
      .mapData(data => {
        send({
          type: Event.UPDATE_DATA,
          payload: {
            allowSocialRegistration: data.item.allow_social_registration,
            linkedPractice: transformPractice(data.included.practice),
            businessCode: null,
          },
        });
      })
      .mapErr(error => {
        if (error.kind === ApiErrorKind.ValidationError) {
          return handleRegistrationValidationError(error, context, send);
        }

        send({
          type: Event.UNHANDLED_ERROR,
          message: IMT(lang.general.registrationError.message, {
            detailedError: genericErrorMessage,
          }),
          error,
        });
      });
  };

const id = 'account-registration';
const config: MachineConfig<Context, StateSchema, EventSchema> = {
  id,
  initial: State.boot,
  context: defaultContext,

  on: {
    [NavigationEvent.BACK]: State.routeFromPath,
    [NavigationEvent.FORWARD]: State.routeFromPath,

    [Event.RESTART]: {
      target: State.linkPractice,
      actions: {type: 'pushTo', route: routes.LinkPractice},
    },
    [Event.RESTART_WITH_SKIP]: {
      // Skip the LinkPractice page and instead go straight to the page given in the event.
      target: `.${State.linkPractice}.${State.connectingBusinessCode}`,
      actions: [{type: 'pushTo', route: routes.TellUsMore}],
    },
    [Event.SKIP_TO_ALREADY_REGISTERED]: {
      target: State.alreadyRegistered,
      actions: {type: 'pushToWithState', route: routes.AlreadyRegistered},
    },
    [Event.SKIP_TO_SOCIAL_ALREADY_REGISTERED]: {
      target: State.alreadyRegisteredSocially,
      actions: {type: 'pushToWithState', route: routes.SocialAlreadyRegistered},
    },
  },

  states: {
    [State.boot]: {
      id: 'boot',
      initial: State.determineBootRequirements,

      on: {
        [Event.NEXT]: State.nextRequirementScreen,
      },

      states: {
        [State.determineBootRequirements]: {
          always: [
            {
              target: '#email-verified',
              cond: everyGuardPasses(
                {type: navigatorParamsHas, parameters: ['token']},
                {type: currentPathMatches, route: routes.EmailConfirmed}
              ),
              actions: {
                type: 'persistDataFromQueryString',
                parameters: ['token'],
              },
            },

            {
              target: State.bootRegistrationProcessWithSkippedRequirements,
              cond: everyGuardPasses(
                {type: navigatorParamsHas, parameters: ['skipRequirements']},
                {
                  type: currentPathMatches,
                  route: [routes.TellUsMore, routes.RegistrationMethods],
                }
              ),
            },

            /**
             * SPECIAL USE CASE: User tries to login but is in an orphaned state.
             *
             * In order to un-orphan them we need to redirect them to the link practice page.
             */
            {
              target: '#link-practice',
              cond: {
                type: 'queryStringHas',
                parameters: ['token', 'orphanedAccount'],
              },
              actions: {
                type: 'persistDataFromQueryString',
                parameters: ['token'],
              },
            },

            /**
             * SPECIAL USE CASE: User tries to login but has not completed their registration yet.
             *
             * A user who has not completed registration and yet tries to login will be redirected here with a token in the query params.
             * This token is to be used to refresh the registration context with all the data necessary to continue their registration.
             * As such, the token needs to be extracted from the query string, sent to the server to hydrate the relevant data, and the
             * user is to be redirected to where they left off in the registration process.
             */
            {
              target: State.bootRegistrationProcessFromToken,
              cond: {type: 'queryStringHas', parameters: ['token']},
              actions: {
                type: 'persistDataFromQueryString',
                parameters: ['token'],
              },
            },
            {target: '#route-from-path'},
          ],
        },

        [State.bootRegistrationProcessFromToken]: {
          invoke: {
            id: 'update-registration',
            src: 'updateRegistration',
            /** If we can't update the registration with a token, then we report the error and land them on the initial screen. */
            onError: {actions: ['reportError', 'goToNextScreen']},
          },
          on: {
            [Event.UPDATE_DATA]: {
              actions: ['updateData', 'goToNextScreen'],
            },
            [Event.UNHANDLED_ERROR]: {
              actions: ['reportError', 'goToNextScreen'],
            },
          },
        },
        [State.bootRegistrationProcessWithSkippedRequirements]: {
          invoke: {
            id: 'boot-registration-process-with-skipped-requirements',
            src: 'bootRegistrationProcessWithSkippedRequirements',
          },
        },
      },
    },

    [State.nextRequirementScreen]: {
      always: [
        // Tell Us More screen needs to be presented before registration methods
        {
          target: State.tellUsMore,
          cond: nextRequirementIs(routes.TellUsMore),
          actions: {type: 'pushTo', route: routes.TellUsMore},
        },
        {
          target: State.registrationMethods,
          cond: 'noRegistrationMethod',
          actions: {type: 'pushTo', route: routes.RegistrationMethods},
        },
        {
          target: State.nameVerification,
          cond: nextRequirementIs(routes.Name),
          actions: {type: 'pushTo', route: routes.Name},
        },
        {
          target: State.birthdate,
          cond: nextRequirementIs(routes.Birthday),
          actions: {type: 'pushTo', route: routes.Birthday},
        },
        {
          target: State.phoneNumber,
          cond: nextRequirementIs(routes.PhoneNumber),
          actions: {type: 'pushTo', route: routes.PhoneNumber},
        },
        {
          target: State.email,
          cond: nextRequirementIs(routes.Email),
          actions: {type: 'pushTo', route: routes.Email},
        },
        {
          target: State.emailVerification,
          cond: nextRequirementIs(routes.ConfirmEmail),
          actions: {type: 'pushTo', route: routes.ConfirmEmail},
        },
        {
          target: State.emailVerified,
          cond: nextRequirementIs(routes.EmailConfirmed),
          actions: {type: 'pushTo', route: routes.ConfirmEmail},
        },
        {
          target: State.username,
          cond: nextRequirementIs(routes.Username),
          actions: {type: 'pushTo', route: routes.Username},
        },
        {
          target: State.password,
          cond: nextRequirementIs(routes.Password),
          actions: {type: 'pushTo', route: routes.Password},
        },
        {
          target: State.success,
          cond: nextRequirementIs(routes.Success),
          actions: {type: 'pushTo', route: routes.Success},
        },
        {
          target: State.alreadyRegistered,
          cond: {type: 'currentPathMatches', route: routes.AlreadyRegistered},
          actions: {type: 'pushToWithState', route: routes.AlreadyRegistered},
        },
        {
          target: State.linkPractice,
          actions: {type: 'pushTo', route: routes.LinkPractice},
        },
      ],
    },

    /**
     * This is a light decoration upon `nextRequirementScreen`.  We would all
     * LOVE to always rely upon `nextRequirementScreen`, however our path here
     * is split in two.  Due to requirements, our dynamically required screens
     * are split between pre/post "Registration Method" selection, and sometimes
     * there are no requirements in between.  Thus, this state exists to check
     * for those pre-requirements and route accordingly.
     */
    [State.nextPreMethodSelectionRequirementScreen]: {
      always: [
        {
          target: State.nextRequirementScreen,
          cond: 'hasPreRegMethodRequirements',
        },
        {
          target: State.registrationMethods,
          actions: {type: 'pushTo', route: routes.RegistrationMethods},
        },
      ],
    },

    [State.routeFromPath]: {
      id: 'route-from-path',
      always: [
        {
          target: State.processingInviteCode,
          cond: {type: 'currentPathMatches', route: routes.InviteCode},
          actions: {
            type: 'persistDataFromQueryString',
            parameters: ['inviteCode'],
          },
        },
        {
          target: State.processingBusinessCode,
          cond: {type: 'currentPathMatches', route: routes.BusinessCode},
          actions: {
            type: 'persistDataFromQueryString',
            parameters: ['businessCode'],
          },
        },
        {
          target: State.linkPractice,
          cond: {
            type: 'currentPathMatches',
            route: routes.LinkPractice,
          },
        },
        {
          target: State.noGenericRegistration,
          cond: {
            type: 'currentPathMatches',
            route: routes.NoGenericRegistration,
          },
        },
        {
          target: State.tellUsMore,
          cond: {
            type: 'currentPathMatches',
            route: routes.TellUsMore,
          },
        },
        {
          target: State.registrationMethods,
          cond: {
            type: 'currentPathMatches',
            route: routes.RegistrationMethods,
          },
        },
        {
          target: State.nameVerification,
          cond: {
            type: 'currentPathMatches',
            route: routes.Name,
          },
        },
        {
          target: State.nameMismatch,
          cond: {
            type: 'currentPathMatches',
            route: routes.NameMismatch,
          },
        },
        {
          target: State.birthdate,
          cond: {
            type: 'currentPathMatches',
            route: routes.Birthday,
          },
        },
        {
          target: State.phoneNumber,
          cond: {
            type: 'currentPathMatches',
            route: routes.PhoneNumber,
          },
        },
        {
          target: State.email,
          cond: {
            type: 'currentPathMatches',
            route: routes.Email,
          },
        },
        {
          target: State.emailVerified,
          cond: {
            type: 'currentPathMatches',
            route: routes.EmailConfirmed,
          },
        },
        {
          target: State.username,
          cond: {
            type: 'currentPathMatches',
            route: routes.Username,
          },
        },
        {
          target: State.password,
          cond: {
            type: 'currentPathMatches',
            route: routes.Password,
          },
        },
        {
          target: State.alreadyRegistered,
          cond: {
            type: 'currentPathMatches',
            route: routes.AlreadyRegistered,
          },
        },
        {
          target: State.alreadyRegisteredSocially,
          cond: {
            type: 'currentPathMatches',
            route: routes.SocialAlreadyRegistered,
          },
        },
        {target: State.linkPractice},
      ],
    },

    [State.linkPractice]: {
      id: 'link-practice',
      type: 'compound',
      initial: State.idle,

      on: {
        [Event.NEXT]: [
          {
            target: State.nextRequirementScreen,
            cond: 'isRegisteringViaSocialProvider',
          },
          {
            target: State.nextPreMethodSelectionRequirementScreen,
          },
        ],
        [Event.GO_TO_GENERIC_REGISTRATION_WARNING]: {
          target: State.noGenericRegistration,
          actions: {type: 'pushTo', route: routes.NoGenericRegistration},
        },
      },

      states: {
        [State.idle]: {
          on: {
            [Event.CONNECT_REWARDS_CARD]: State.connectingRewardsCard,
            [Event.CONNECT_BUSINESS_CODE]: State.connectingBusinessCode,
            [Event.UPDATE_DATA]: {
              actions: ['clearValidationErrors', 'updateData'],
            },
            [Event.HAS_VALIDATION_ERRORS]: {actions: 'updateValidationErrors'},
          },
        },

        [State.connectingRewardsCard]: {
          invoke: {
            id: 'connect-rewards-card',
            src: 'connectRewardsCard',
            onError: {target: State.idle, actions: 'reportError'},
          },
          on: {
            [Event.UPDATE_DATA]: {
              target: State.handleSocialRegistrationOrProceed,
              actions: 'updateData',
            },
            [Event.HAS_VALIDATION_ERRORS]: {
              target: State.idle,
              actions: 'updateValidationErrors',
            },
            [Event.UNHANDLED_ERROR]: State.idle,
          },
        },

        [State.connectingBusinessCode]: {
          invoke: {
            id: 'connect-business-code',
            src: 'connectBusinessCode',
            onError: {target: State.idle, actions: 'reportError'},
          },
          on: {
            [Event.UPDATE_DATA]: {
              target: State.handleSocialRegistrationOrProceed,
              actions: ['updateData'],
            },
            [Event.HAS_VALIDATION_ERRORS]: {
              target: State.idle,
              actions: 'updateValidationErrors',
            },
            [Event.UNHANDLED_ERROR]: State.idle,
          },
        },

        [State.handleSocialRegistrationOrProceed]: {
          always: [
            /**
             * SPECIAL USE CASE: User tries to login via social, but has not completed their registration yet.
             * At some point, the user that meets this condition has:
             *   A) registered at least some of the steps beyond just linking a practice
             *   B) selected a social provider as their registration method
             *   C) abandoned their registration.
             *
             * After having abandoned registration, they tried to login via social. We caught that attempt, and prompted them to continue their registration here.
             * The query string will have both a token and a flag indicating they have an incomplete registration. Once they land here, we:
             *   A) Boot up their registration with the provided token
             *   B) We use their name provided by the social provider and verify it
             *   C) Push them to where they left off in the registration process.
             */
            {
              target: State.bootSocialRegisteredProcessFromToken,
              cond: {
                type: 'queryStringHas',
                parameters: ['token', 'linkingViaSocial'],
                transform: {
                  linkingViaSocial: (value: string) => Boolean(Number(value)),
                },
              },
              actions: {
                type: 'persistDataFromQueryString',
                parameters: ['token', 'linkingViaSocial'],
                rekey: {
                  linkingViaSocial: {
                    to: 'linkedViaSocial',
                    transform: (value: string) => Boolean(Number(value)),
                  },
                },
              },
            },

            {target: State.idle, actions: 'goToNextScreen'},
          ],
        },

        [State.bootSocialRegisteredProcessFromToken]: {
          invoke: {
            src: nameVerificationMachine,
            data: (context: Context) => ({
              apiClient: context.apiClient,
              token: context.token,
              firstname: context.account.firstname,
              lastname: context.account.lastname,
              cardNumber: cleanCardNumber(context.cardNumber),
              inviteCode: context.inviteCode,
            }),
            onDone: {
              target: State.idle,
              actions: 'goToNextScreen',
            },
            onError: {
              target: State.idle,
              actions: 'reportError',
            },
          },
          on: {
            [Event.UPDATE_DATA]: {
              actions: 'updateData',
            },
            [Event.HAS_VALIDATION_ERRORS]: {
              target: State.idle,
              actions: 'updateValidationErrors',
            },
            [Event.UNHANDLED_ERROR]: {
              target: State.idle,
              actions: 'reportError',
            },
          },
        },
      },
    },

    [State.tellUsMore]: {
      id: 'tellUsMore',
      type: 'compound',
      initial: State.idle,
      on: {
        [Event.NEXT]: {
          target: State.registrationMethods,
          actions: {
            type: 'pushTo',
            route: routes.RegistrationMethods,
          },
        },
      },
      states: {
        [State.idle]: {
          on: {
            [Event.UPDATE_DATA]: {
              actions: ['clearValidationErrors', 'updateData'],
            },
            [Event.HAS_VALIDATION_ERRORS]: {
              actions: 'updateValidationErrors',
            },
            [Event.UPDATE_REGISTRATION]: State.submittingLeadSourceInfo,
          },
        },
        [State.submittingLeadSourceInfo]: {
          invoke: {
            id: 'update-registration',
            src: 'updateRegistration',
            onError: {target: State.idle, actions: 'reportError'},
          },
          on: {
            [Event.UPDATE_DATA]: {
              target: State.idle,
              actions: ['updateData', 'goToNextScreen'],
            },
            [Event.HAS_VALIDATION_ERRORS]: {
              target: State.idle,
              actions: 'updateValidationErrors',
            },
            [Event.UNHANDLED_ERROR]: {
              target: State.idle,
              actions: 'reportError',
            },
          },
        },
      },
    },

    [State.registrationMethods]: {
      id: 'registration-methods',
      initial: State.boot,

      on: {
        [Event.UPDATE_REGISTRATION_METHOD]: {
          actions: 'updateRegistrationMethod',
        },

        [Event.NEXT]: [
          {
            target: State.nextRequirementScreen,
            cond: 'isRegisteringViaSocialProvider',
          },
          {
            target: State.nameVerification,
            actions: {type: 'pushTo', route: routes.Name},
          },
        ],
        [Event.NAME_NOT_MATCHED]: {
          target: State.nameMismatch,
          actions: {type: 'pushTo', route: routes.NameMismatch},
        },
      },

      states: {
        [State.boot]: {
          always: [
            {
              target: State.bootRegistrationProcessFromUnregisteredInput,
              cond: {
                type: 'queryStringHas',
                parameters: ['unregisteredInput'],
              },
              actions: {
                type: 'persistDataFromQueryString',
                parameters: ['unregisteredInput'],
                rekey: {
                  unregisteredInput: {
                    to: 'cardNumber',
                    transform: (cardNumber: string) =>
                      cleanCardNumber(cardNumber),
                  },
                },
              },
            },
            {target: State.idle},
          ],
        },
        [State.idle]: {
          on: {
            [Event.LOGIN_VIA_SOCIAL]: {
              actions: 'loginViaSocialProvider',
            },
            [Event.UPDATE_REGISTRATION]: {
              target: State.updateRegistrationViaSocial,
              actions: 'updateData',
            },
          },
        },
        [State.updateRegistrationViaSocial]: {
          invoke: {
            src: nameVerificationMachine,
            data: (context: Context) => ({
              apiClient: context.apiClient,
              token: context.token,
              firstname: context.account.firstname,
              lastname: context.account.lastname,
              cardNumber: cleanCardNumber(context.cardNumber),
              inviteCode: context.inviteCode,
            }),
            onDone: {
              target: State.idle,
              actions: 'goToNextScreen',
            },
            onError: {
              target: State.idle,
              actions: 'reportError',
            },
          },
          on: {
            [Event.UPDATE_DATA]: {
              actions: 'updateData',
            },
            [Event.HAS_VALIDATION_ERRORS]: [
              {
                target: State.idle,
                cond: 'nameWasOmittedFromIncomingSocialAccount',
                actions: 'goToNextScreen',
              },
              {
                target: State.idle,
                actions: 'updateValidationErrors',
              },
            ],
            [Event.UNHANDLED_ERROR]: {
              target: State.idle,
              actions: 'reportError',
            },
          },
        },
        [State.bootRegistrationProcessFromUnregisteredInput]: {
          always: [
            {
              target: State.bootRegistrationProcessFromUnregisteredCardNumber,
              cond: context => Boolean(context.cardNumber),
            },
            {
              target: State.bootRegistrationProcessFromUnregisteredBusinessCode,
            },
          ],
        },
        [State.bootRegistrationProcessFromUnregisteredCardNumber]: {
          invoke: {
            src: 'connectRewardsCard',
            onError: bootUserConnectionOnError,
          },
          on: {
            [Event.UPDATE_DATA]: bootUserConnectionOnUpdateData,
            [Event.HAS_VALIDATION_ERRORS]:
              bootUserConnectionOnHasValidationErrors,
            [Event.UNHANDLED_ERROR]: bootUserConnectionOnError,
          },
        },
        [State.bootRegistrationProcessFromUnregisteredBusinessCode]: {
          invoke: {
            src: 'connectBusinessCode',
            onError: bootUserConnectionOnError,
          },
          on: {
            [Event.UPDATE_DATA]: bootUserConnectionOnUpdateData,
            [Event.HAS_VALIDATION_ERRORS]:
              bootUserConnectionOnHasValidationErrors,
            [Event.UNHANDLED_ERROR]: bootUserConnectionOnError,
          },
        },
      },
    },

    [State.processingInviteCode]: {
      invoke: {
        src: 'connectInviteCode',
        onError: bootUserConnectionOnError,
      },
      on: {
        [Event.UPDATE_DATA]: {
          target: State.registrationMethods,
          actions: 'updateData',
        },
        [Event.HAS_VALIDATION_ERRORS]: bootUserConnectionOnHasValidationErrors,
        [Event.UNHANDLED_ERROR]: bootUserConnectionOnError,
      },
    },

    [State.processingBusinessCode]: {
      invoke: {
        src: 'connectBusinessCode',
        onError: bootUserConnectionOnError,
      },
      on: {
        [Event.UPDATE_DATA]: {
          target: State.nextPreMethodSelectionRequirementScreen,
          actions: ['updateData'],
        },
        [Event.HAS_VALIDATION_ERRORS]: bootUserConnectionOnHasValidationErrors,
        [Event.UNHANDLED_ERROR]: bootUserConnectionOnError,
      },
    },

    [State.noGenericRegistration]: {},

    [State.nameVerification]: {
      id: 'name-verification',
      type: 'compound',
      initial: State.idle,

      on: {
        [Event.NEXT]: {
          target: State.nextRequirementScreen,
        },
        [Event.NAME_NOT_MATCHED]: {
          target: State.nameMismatch,
          actions: {type: 'pushTo', route: routes.NameMismatch},
        },
      },

      states: {
        [State.idle]: {
          on: {
            [Event.UPDATE_REGISTRATION]: State.verifyingName,
            [Event.UPDATE_DATA]: {
              actions: ['clearValidationErrors', 'updateData'],
            },
            [Event.HAS_VALIDATION_ERRORS]: {actions: 'updateValidationErrors'},
          },
        },

        [State.verifyingName]: {
          invoke: {
            src: nameVerificationMachine,
            data: (context: Context) => ({
              apiClient: context.apiClient,
              token: context.token,
              firstname: context.account.firstname,
              lastname: context.account.lastname,
              cardNumber: cleanCardNumber(context.cardNumber),
              inviteCode: context.inviteCode,
            }),
            onDone: {
              target: State.idle,
              actions: 'goToNextScreen',
            },
            onError: {
              target: State.idle,
              actions: 'reportError',
            },
          },
          on: {
            [Event.UPDATE_DATA]: {
              actions: 'updateData',
            },
            [Event.HAS_VALIDATION_ERRORS]: {
              target: State.idle,
              actions: 'updateValidationErrors',
            },
            [Event.UNHANDLED_ERROR]: {
              target: State.idle,
              actions: 'reportError',
            },
          },
        },
      },
    },

    [State.nameMismatch]: {
      on: {
        [Event.RETRY_NAME_VERIFICATION]: {
          target: State.nameVerification,
          actions: {type: 'replaceRouteWith', route: routes.Name},
        },
      },
    },

    [State.birthdate]: {
      id: 'birthdate',
      type: 'compound',
      initial: State.idle,

      on: {
        [Event.NEXT]: {
          target: State.nextRequirementScreen,
        },
      },

      states: {
        [State.idle]: {
          on: {
            [Event.UPDATE_REGISTRATION]: State.submittingBirthdate,
            [Event.UPDATE_DATA]: {
              actions: ['clearValidationErrors', 'updateData'],
            },
            [Event.HAS_VALIDATION_ERRORS]: {
              actions: 'updateValidationErrors',
            },
          },
        },

        [State.submittingBirthdate]: {
          invoke: {
            id: 'update-registration',
            src: 'updateRegistration',
            onError: {target: State.idle, actions: 'reportError'},
          },
          on: {
            [Event.UPDATE_DATA]: {
              target: State.idle,
              actions: ['updateData', 'goToNextScreen'],
            },
            [Event.HAS_VALIDATION_ERRORS]: {
              target: State.idle,
              actions: 'updateValidationErrors',
            },
            [Event.UNHANDLED_ERROR]: {
              target: State.idle,
              actions: 'reportError',
            },
          },
        },
      },
    },

    [State.phoneNumber]: {
      id: 'birthdate',
      type: 'compound',
      initial: State.idle,

      on: {
        [Event.NEXT]: {
          target: State.nextRequirementScreen,
        },
      },

      states: {
        [State.idle]: {
          on: {
            [Event.UPDATE_REGISTRATION]: State.submittingPhoneNumber,
            [Event.UPDATE_DATA]: {
              actions: ['clearValidationErrors', 'updateData'],
            },
            [Event.HAS_VALIDATION_ERRORS]: {
              actions: 'updateValidationErrors',
            },
          },
        },

        [State.submittingPhoneNumber]: {
          invoke: {
            id: 'update-registration',
            src: 'updateRegistration',
            onError: {target: State.idle, actions: 'reportError'},
          },
          on: {
            [Event.UPDATE_DATA]: {
              target: State.idle,
              actions: ['updateData', 'goToNextScreen'],
            },
            [Event.HAS_VALIDATION_ERRORS]: {
              target: State.idle,
              actions: 'updateValidationErrors',
            },
            [Event.UNHANDLED_ERROR]: {
              target: State.idle,
              actions: 'reportError',
            },
          },
        },
      },
    },

    [State.email]: {
      id: 'email',
      type: 'compound',
      initial: State.idle,

      on: {
        [Event.SKIP_TO_EMAIL_CONFIRMED]: {
          target: State.emailVerified,
          actions: {type: 'pushTo', route: routes.EmailConfirmed},
        },
        [Event.NEXT]: {
          target: State.nextRequirementScreen,
        },
      },

      states: {
        [State.idle]: {
          on: {
            [Event.UPDATE_REGISTRATION]: State.submittingEmail,
            [Event.UPDATE_DATA]: {
              actions: ['clearValidationErrors', 'updateData'],
            },
            [Event.HAS_VALIDATION_ERRORS]: {
              actions: 'updateValidationErrors',
            },
          },
        },

        [State.submittingEmail]: {
          invoke: {
            id: 'update-registration',
            src: 'updateRegistration',
            onError: {target: State.idle, actions: 'reportError'},
          },
          on: {
            [Event.UPDATE_DATA]: {
              target: State.idle,
              actions: ['updateData', 'goToNextScreen'],
            },
            [Event.HAS_VALIDATION_ERRORS]: {
              target: State.idle,
              actions: 'updateValidationErrors',
            },
            [Event.UNHANDLED_ERROR]: {
              target: State.idle,
              actions: 'reportError',
            },
          },
        },
      },
    },

    [State.emailVerification]: {
      id: 'email-verification',
      type: 'compound',
      initial: State.idle,

      on: {
        [Event.NEXT]: {
          target: State.emailVerified,
          actions: {type: 'pushTo', route: routes.EmailConfirmed},
        },
      },

      states: {
        [State.idle]: {
          on: {
            [Event.SUBMIT_VERIFICATION_CODE]: State.submittingVerificationCode,
            [Event.MARK_EMAIL_VERIFIED]: {
              actions: ['markEmailVerified', 'goToNextScreen'],
            },
            [Event.RESEND_VERIFICATION_EMAIL]: State.resendingVerificationEmail,
            [Event.UPDATE_DATA]: {
              actions: ['clearValidationErrors', 'updateData'],
            },
            [Event.HAS_VALIDATION_ERRORS]: {
              actions: 'updateValidationErrors',
            },
          },
        },

        [State.submittingVerificationCode]: {
          invoke: {
            id: 'verify-email',
            src: 'verifyEmail',
            onError: {target: State.idle, actions: 'reportError'},
          },
          on: {
            [Event.UPDATE_DATA]: {
              target: State.idle,
              actions: 'updateData',
            },
            [Event.HAS_VALIDATION_ERRORS]: {
              target: State.idle,
              actions: 'updateValidationErrors',
            },
            [Event.UNHANDLED_ERROR]: State.idle,
          },
        },

        [State.resendingVerificationEmail]: {
          invoke: {
            id: 'resend-verification-email',
            src: 'resendVerificationEmail',
            onDone: State.idle,
            onError: {target: State.idle, actions: 'reportError'},
          },
        },
      },
    },

    [State.emailVerified]: {
      id: 'email-verified',
      initial: State.boot,

      on: {
        // Note: This is a special edge case where we don't want a user to land on the email verification page, rather they should end up on the email page.
        [NavigationEvent.BACK]: {
          target: State.email,
          actions: {type: 'replaceRouteWith', route: routes.Email},
        },
        [Event.SKIP_TO_SUCCESS]: {
          target: State.success,
          actions: {type: 'pushTo', route: routes.Success},
        },
        [Event.NEXT]: State.nextRequirementScreen,
      },

      states: {
        [State.boot]: {
          entry: 'maybeResumeRegistration',
          on: {
            [Event.MAYBE_RESUME_REGISTRATION]: [
              {
                target: State.resumingRegistration,
                cond: {type: 'queryStringHas', parameters: ['token']},
              },
              {target: State.idle},
            ],
          },
        },

        [State.idle]: {
          on: {
            [Event.CHECK_REGISTRATION_STATUS]: State.checkingRegistrationStatus,
          },
        },

        [State.resumingRegistration]: {
          invoke: {
            src: 'updateRegistration',
            onError: {target: State.idle, actions: 'reportError'},
          },
          on: {
            [Event.UPDATE_DATA]: {
              target: State.idle,
              actions: ['updateData', 'goToNextScreen'],
            },
            [Event.UNHANDLED_ERROR]: {
              target: State.idle,
              actions: 'reportError',
            },
          },
        },

        [State.checkingRegistrationStatus]: {
          invoke: {
            id: 'check-registration-status',
            src: 'checkRegistrationStatus',
            onError: {target: State.idle, actions: 'reportError'},
          },
          on: {
            [Event.UPDATE_DATA]: {
              target: State.idle,
              actions: ['updateData', 'goToNextScreen'],
            },
          },
        },
      },
    },

    [State.username]: {
      id: 'username',
      initial: State.idle,

      on: {
        [Event.NEXT]: {
          target: State.nextRequirementScreen,
        },
      },

      states: {
        [State.idle]: {
          on: {
            [Event.UPDATE_REGISTRATION]: State.submittingUsername,
            [Event.UPDATE_DATA]: {
              actions: ['clearValidationErrors', 'updateData'],
            },
            [Event.HAS_VALIDATION_ERRORS]: {
              actions: 'updateValidationErrors',
            },
          },
        },

        [State.submittingUsername]: {
          invoke: {
            id: 'update-registration',
            src: 'updateRegistration',
            onError: {target: State.idle, actions: 'reportError'},
          },
          on: {
            [Event.UPDATE_DATA]: {
              target: State.idle,
              actions: ['updateData', 'goToNextScreen'],
            },
            [Event.HAS_VALIDATION_ERRORS]: {
              target: State.idle,
              actions: 'updateValidationErrors',
            },
            [Event.UNHANDLED_ERROR]: {
              target: State.idle,
              actions: 'reportError',
            },
          },
        },
      },
    },

    [State.password]: {
      id: 'password',
      initial: State.idle,

      on: {
        [Event.NEXT]: {
          target: State.nextRequirementScreen,
        },
      },

      states: {
        [State.idle]: {
          on: {
            [Event.UPDATE_REGISTRATION]: State.submittingPassword,
            [Event.UPDATE_DATA]: {
              actions: ['clearValidationErrors', 'updateData'],
            },
            [Event.HAS_VALIDATION_ERRORS]: {
              actions: 'updateValidationErrors',
            },
          },
        },

        [State.submittingPassword]: {
          invoke: {
            id: 'update-registration',
            src: 'updateRegistration',
            onError: {target: State.idle, actions: 'reportError'},
          },
          on: {
            [Event.UPDATE_DATA]: {
              target: State.idle,
              actions: ['updateData', 'goToNextScreen'],
            },
            [Event.HAS_VALIDATION_ERRORS]: {
              target: State.idle,
              actions: 'updateValidationErrors',
            },
            [Event.UNHANDLED_ERROR]: {
              target: State.idle,
              actions: 'reportError',
            },
          },
        },
      },
    },

    // These are "final" states, but because you can go forward/back with the browser, you can't officially set these as "final"
    [State.success]: {
      id: 'success',
      initial: State.idle,

      states: {
        [State.idle]: {
          on: {
            [Event.GO_TO_ACCOUNT]: [
              {
                cond: 'isMissingAuthorizationTokens',
                actions: 'redirectToLoginWithErrors',
              },
              {target: State.loggingInViaOauthTokens},
            ],
          },
        },

        [State.loggingInViaOauthTokens]: {
          invoke: {
            id: 'login-via-oauth-tokens',
            src: 'loginViaOauthTokens',
            onError: {target: State.idle, actions: 'reportError'},
          },
          on: {
            [Event.HAS_VALIDATION_ERRORS]: {
              target: State.idle,
              actions: 'updateValidationErrors',
            },
          },
        },
      },
    },

    [State.alreadyRegistered]: {
      on: {
        [Event.GO_TO_LOGIN]: {
          actions: 'handleExit',
        },
        [Event.SKIP_TO_EMAIL]: {
          target: State.email,
          actions: {type: 'pushTo', route: routes.Email},
        },
      },
    },

    // The SocialAlreadyRegistered.js component handles login logic within it's <SocialButtons /> component.
    [State.alreadyRegisteredSocially]: {
      on: {
        [Event.GO_TO_LOGIN]: {
          actions: 'handleExit',
        },
        [Event.SKIP_TO_EMAIL]: {
          target: State.email,
          actions: {type: 'pushTo', route: routes.Email},
        },
      },
    },
  },
};

// @ts-ignore - XState types are incredibly zonked.  It thinks there's something wrong with the `invoke` of `State.bootRegistrationProcessFromToken`
const machine = createXstateMachine(config, {
  guards: {
    currentPathMatches,

    noRegistrationMethod: context => !context.registrationMethod,
    isRegisteringViaSocialProvider: context =>
      Boolean(context.linkedViaSocial && context.socialProvider),

    isRegisteringWithBusinessCode: context => Boolean(context.businessCode),

    hasPreRegMethodRequirements: context =>
      remainingPreRegistrationMethodRequirements(
        context.remainingRequirements ?? []
      ).length > 0,

    nameWasOmittedFromIncomingSocialAccount: (context, event) =>
      event.type === Event.HAS_VALIDATION_ERRORS &&
      ('firstname' in event.errors || 'lastname' in event.errors),

    isMissingAuthorizationTokens: context => !context.authorization,

    queryStringHas: navigatorParamsHas,
  },

  actions: {
    pushTo: (context, event, meta) => {
      context.navigator.navigate(meta.action.route);
    },

    pushToWithState: (context, event, meta) => {
      context.navigator.navigate(meta.action.route, R.omit(['type'], event));
    },

    replaceRouteWith: (context, event, meta) => {
      context.navigator.replace(meta.action.route);
    },

    // @ts-ignore - TS doesn't like this :(
    maybeResumeRegistration: send({
      type: Event.MAYBE_RESUME_REGISTRATION,
      include: ['practices'],
    }),

    updateRegistrationMethod: assign((context, event) => {
      if (event.type !== Event.UPDATE_REGISTRATION_METHOD) return context;

      return {
        registrationMethod: event.payload,
      };
    }),

    persistDataFromQueryString: assign((context, event, meta) => {
      const queryParams = R.pick(
        // @ts-ignore - Trust us TS, it's there 😁
        R.prop('parameters', meta.action),
        context.navigator.location.params
      );

      /**
       * Given an object of rekey instructions, we rekey our query param values to match what's in context.
       * For example: rekey: {token: {to: 'greatToken'}} will remap the `token` param from the query string to {greatToken: [tokenValueFromQueryStringHere]}
       *
       * An optional transform function can be applied to the value as well:
       * For example: rekey: {token: {to: 'greatToken', transform: value => value.toUpperCase()}} would give us {greatToken: 'A.GREAT.TOKEN}.
       */
      type RekeyConfig = {
        to: string;
        test?: RegExp;
        transform?: (value: string) => unknown;
      };

      const rekey: Record<string, RekeyConfig[] | RekeyConfig> = R.propOr(
        {},
        'rekey',
        meta.action
      );

      const unmappedQueryParamData = R.omit(R.keys(rekey), queryParams);
      const remappedQueryParamData = R.fromPairs(
        R.map(paramName => {
          let to;
          let transform;

          const config = rekey[paramName];
          const value = queryParams[paramName] as string;

          if (Array.isArray(config)) {
            const configsWithTests = R.filter(R.has('test'), config) as Require<
              RekeyConfig,
              'test'
            >[];

            const fallbackConfig = R.head(R.reject(R.has('test'), config));

            if (!fallbackConfig) {
              throw new InvariantError(
                'Tried to rekey query parameters using multiple conditions without a fallback condition. Check the logic of "persistDataFromQueryString".'
              );
            }

            // @ts-expect-error
            ({to, transform = R.identity} =
              R.find(rk => rk.test.test(value), configsWithTests) ??
              fallbackConfig);
          } else {
            ({to, transform = R.identity} = config);
          }

          return [to, transform(value)];
        }, R.keys(rekey))
      );

      return R.mergeAll([
        context,
        unmappedQueryParamData,
        remappedQueryParamData,
      ]);
    }),

    updateData: assign((context, event) =>
      'payload' in event && typeof event.payload !== 'undefined'
        ? (R.mergeDeepRight(context, event.payload) as Context)
        : context
    ),

    updateValidationErrors: assign((context, event) =>
      event.type !== Event.HAS_VALIDATION_ERRORS
        ? context
        : R.assoc(
            'validationErrors',
            R.mergeDeepRight(R.prop('validationErrors', context), event.errors),
            context
          )
    ),

    // @ts-ignore
    clearValidationErrors: assign<Context>({validationErrors: {}}),

    goToNextScreen: send(Event.NEXT),

    markEmailVerified: assign((context, event) => {
      if (event.type !== Event.MARK_EMAIL_VERIFIED) return context;
      const oppositeEmailField =
        event.field === 'responsibleEmail' ? 'email' : 'responsibleEmail';

      return R.pipe(
        // Mark the email under verification as verified
        R.assocPath<RegistrationData['account']['email'], Context>(
          ['confirmedEmail', event.field],
          context.account[event.field]
        ),
        // Mark the opposite email under verification as un-verified
        R.assocPath(
          ['confirmedEmail', oppositeEmailField],
          context.account[oppositeEmailField]
        ),
        /**
         * The requirment here needs to be manually removed in the case of verification over websocket.
         * If we don't remove the verification requirement and try to move on, we'll stay stuck on this screen
         * because we haven't consulted the server for updated requirements.
         */
        (context: Context) =>
          R.assoc(
            'remainingRequirements',
            R.reject(
              R.equals(`${snakeCase(event.field)}_verification`),
              context.remainingRequirements
            ),
            context
          )
      )(context) as Context;
    }),

    loginViaSocialProvider: (context, event) => {
      const socialProvider =
        event.type === Event.LOGIN_VIA_SOCIAL
          ? event.socialProvider
          : context.socialProvider;

      if (!socialProvider) {
        throw new InvariantError(
          `Tried to login via social provider with no provider specified!`
        );
      }

      context.redirect.toSocialLoginWithProvider(socialProvider);
    },

    redirectToLoginWithErrors: context => {
      context.logger.error(
        'Attempted to log in with no registration authorization tokens'
      );
      errorAndRedirect(context.showAlert, context.redirect.toLogin);
    },

    handleExit: (context, event) => {
      if (event.type === Event.GO_TO_LOGIN) {
        const hint = context.navigator.location.state
          .hint as AlreadyRegisteredHint;
        const email = encodeURIComponent(hint?.email ?? '');

        context.redirect.toLoginAsUser(email);
      }
    },

    reportError: (context, event) => {
      context.logger.error(
        'message' in event && event.message
          ? event.message
          : 'An error has occurred during the registration process.',
        'error' in event ? event.error : event
      );
    },
  },

  services: {
    connectRewardsCard: context =>
      makeConnectUserIdentifierService(
        context,
        '/global_users/registration/rewards_card_verification',
        {card_number: cleanCardNumber(context.cardNumber)},
        lang.general.registrationError.failedConnectingRewardsCard
      ),

    connectInviteCode: context =>
      makeConnectUserIdentifierService(
        context,
        '/global_users/registration/invite_code_verification',
        {invite_code: context.inviteCode},
        lang.general.registrationError.failedConnectingInvite
      ),

    connectBusinessCode: (context, event) => async send => {
      const businessCode: string = (() => {
        // context.businessCode houses the business code as the user has entered it (only for generic hub registration) and takes precedence here.
        if (context.businessCode) return context.businessCode;
        // For non-generic hub registration or restarts, the businessCode will be give to use via the event, so we fall back to that.
        if ('businessCode' in event) return event.businessCode;
        return '';
      })();
      (
        await context.apiClient.post(
          '/global_users/registration/business_code',
          {business_code: businessCode}
        )
      )
        .mapData(data => {
          send({
            type: Event.UPDATE_DATA,
            payload: {
              ...transformRegistration(data),
              businessCode,
              cardNumber: null,
            },
          });

          if (
            'skipTo' in event &&
            event.skipTo === routes.RegistrationMethods
          ) {
            // Hardcode responses to the "Tell Us More" screen to bypass this for the user
            send({
              type: Event.UPDATE_REGISTRATION,
              payload: {
                relationship: AccountRelationship.other,
                why: RegistrationReason.accessAccount,
                questionsOrComments: 'Skipped to Registration Methods screen',
              },
            });
          }
        })
        .mapErr(error => {
          if (error.kind === ApiErrorKind.ValidationError) {
            send({
              type: Event.HAS_VALIDATION_ERRORS,
              errors: objectKeysToCamelCase(error.errors),
            });
          }

          send({
            type: Event.UNHANDLED_ERROR,
            message: IMT(lang.general.registrationError.message, {
              detailedError:
                lang.general.registrationError.failedConnectingBusinessCode,
            }),
            error,
          });
        });
    },

    // @ts-ignore
    updateRegistration: sharedMachineOptions.services
      .updateRegistration as ServiceConfig<Context>,

    verifyEmail: context => async send => {
      (
        await context.apiClient.post(
          `/global_users/contacts/${context.account.contactId}/verify`,
          {code: context.emailVerificationCode}
        )
      )
        .mapData(() => {
          const emailFieldUnderVerification = context.account
            .useResponsibleEmail
            ? 'responsibleEmail'
            : 'email';

          send({
            type: Event.UPDATE_DATA,
            payload: {
              emailVerificationCode: '',
            },
          });

          send({
            type: Event.MARK_EMAIL_VERIFIED,
            field: emailFieldUnderVerification,
          });
        })
        .mapErr(error => {
          switch (error.kind) {
            case ApiErrorKind.ValidationError:
              return send({
                type: Event.HAS_VALIDATION_ERRORS,
                errors: {
                  // @ts-ignore
                  emailVerificationCode: error.errors.code as string,
                },
              });

            case ApiErrorKind.RateLimitError:
              return send({
                type: Event.HAS_VALIDATION_ERRORS,
                errors: {
                  emailVerificationCode:
                    lang.general.validation.tooManyRequests,
                },
              });
            default:
              send({
                type: Event.UNHANDLED_ERROR,
                message: IMT(lang.general.registrationError.message, {
                  detailedError:
                    lang.general.registrationError.failedConnectingBusinessCode,
                }),
                error,
              });
          }
        });
    },

    resendVerificationEmail: context =>
      context.apiClient.post(
        '/global_users/registration/send_contact_verification',
        {token: context.token}
      ),

    checkRegistrationStatus: context => async send => {
      (
        await context.apiClient.get(
          `/global_users/registration/${context.token}`
        )
      )
        .mapData(data => {
          const {
            item: {registration},
          } = data;

          /**
           * This exists for the following reasons:
           *
           *   As user is underage we require parent's email
           *   Once the email is confirmed, (The non-social flow expects user to set the password after email confirmed) but with social account register there is no requirements left.
           *   We need to detect that and send user to success screen instead of Account already Registered screen.
           *
           * @see https://github.com/practicegenius/sites.PatientRewardsHub.com-2.0/pull/1259
           */
          if (
            context.account.useResponsibleEmail &&
            context.linkedViaSocial &&
            registration.completed
          ) {
            return send(Event.SKIP_TO_SUCCESS);
          }
          if (registration.completed) {
            return send(Event.SKIP_TO_ALREADY_REGISTERED);
          }
          send({
            type: Event.UPDATE_DATA,
            payload: {...registration, draftAccount: context.account},
          });
        })
        .mapErr(error => {
          send({
            type: Event.UNHANDLED_ERROR,
            message: IMT(lang.general.registrationError.message, {
              detailedError:
                lang.general.registrationError.unableToCheckRegistrationStatus,
            }),
            error,
          });
        });
    },

    loginViaOauthTokens: context => async send => {
      const {authorization: tokens, practiceUserRecord} = context;

      (
        await context.sitesDotClient.post('/login', {
          remember_me: Number(context.rememberMe),
          // We could not have gotten here if authorization was null, therefore we can assert non-null here.
          /* eslint-disable @typescript-eslint/no-non-null-assertion */
          token_type: tokens!.tokenType,
          expires_in: tokens!.expiresIn,
          access_token: tokens!.accessToken,
          refresh_token: tokens!.refreshToken,
          practice_record_id: practiceUserRecord.id,
          /* eslint-enable @typescript-eslint/no-non-null-assertion */
        })
      )
        .mapData(data => {
          if (!data.success) {
            send({
              type: Event.HAS_VALIDATION_ERRORS,
              errors: objectKeysToCamelCase(data.errors),
            });
          } else if (context.redirect.toAnywhere) {
            const {redirect} = data;

            /**
             * Login unfortunately sends down either a fully qualified url with
             * protocol, or a relative url that may be missing the "/" prefix.
             */
            const transformedRedirect = /^http/.test(redirect)
              ? redirect
              : `/${redirect}`.replace('//', '/');

            context.redirect.toAnywhere(transformedRedirect);
          }
        })
        .mapErr(error => {
          context.logger.error(
            'Unable to log in via registration authorization tokens',
            error.kind === ApiErrorKind.ValidationError ? error.errors : error
          );
          errorAndRedirect(context.showAlert, context.redirect.toLogin);
        });
    },
    bootRegistrationProcessWithSkippedRequirements: () => send => {
      const route = parseRegistrationRoute(window.location.pathname);
      if (route && routeCanBeSkippedTo(route)) {
        send({
          type: Event.RESTART_WITH_SKIP,
          businessCode:
            // @ts-ignore not wasting time on typing this
            window.phpJs.practice_code,
          skipTo: route,
        });
      } else {
        send({
          type: Event.RESTART,
        });
      }
    },
  },
});

const createMachine = (ctx: Partial<Context>) =>
  machine.withContext({...defaultContext, ...ctx});

export type ActorType = Interpreter<Context, StateSchema, EventSchema>;
export {
  Event as RegistrationEvent,
  State as RegistrationState,
  machine as registrationMachine,
  defaultContext as registrationDefaultContext,
  id as registrationMachineId,
  createMachine as createRegistrationMachine,
};
